// ─────────────────────────────────────────────
//  Bulk login-account creation for seeded user profiles.
//
//  Seeded `users` docs (from seedDoctors/seedProviders) have random doc-ids and
//  no Firebase Auth account. This creates an Auth account for each (with a
//  throwaway random password — users never see it) and RE-KEYS the profile doc
//  to users/{authUid} so the first sign-in finds it instead of creating a
//  duplicate default profile. No emails are sent: users set their own password
//  via the login page "Forgot password" reset link.
// ─────────────────────────────────────────────
import { setDoc, deleteDoc, doc } from "./sdk";
import { db } from "./config";
import { createUserAccount } from "./auth";

// Throwaway password the user never sees — long + mixed so it satisfies any
// password policy. They replace it via the reset-link flow.
export function randomPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%";
  const arr = new Uint32Array(20);
  (globalThis.crypto || globalThis.msCrypto).getRandomValues(arr);
  let out = "";
  for (let i = 0; i < arr.length; i += 1) out += chars[arr[i] % chars.length];
  return out;
}

// A profile needs an account when it has an email and is NOT already keyed by
// its auth uid. Real profiles are created via createUser(uid) so doc.id === uid;
// seeded docs (addDoc) have a random id and no uid field.
export function needsAccount(u) {
  return !!(u && u.email && (!u.uid || u.uid !== u.id));
}

// Create accounts for every profile that needs one, re-keying as we go.
// onProgress({ done, total }) fires after each attempt. Returns a summary
// { total, created, skipped, failed: [{name,email,code}] }.
export async function bulkCreateLoginAccounts(users, onProgress) {
  const targets = (users || []).filter(needsAccount);
  const summary = { total: targets.length, created: 0, skipped: 0, failed: [] };

  for (let i = 0; i < targets.length; i += 1) {
    const u = targets[i];
    try {
      const email = u.email.trim().toLowerCase();
      const newUid = await createUserAccount(email, randomPassword());
      const { id, ...data } = u;
      await setDoc(doc(db, "users", newUid), { ...data, uid: newUid });
      if (id && id !== newUid) await deleteDoc(doc(db, "users", id));
      summary.created += 1;
    } catch (err) {
      const code = err?.code || "error";
      // Account already exists (re-run, or someone signed in already) — leave it.
      if (code === "auth/email-already-in-use") summary.skipped += 1;
      else summary.failed.push({ name: u.name, email: u.email, code });
    } finally {
      if (onProgress) onProgress({ done: i + 1, total: targets.length });
    }
  }
  return summary;
}
